/**
 * LeatherSliceController — the Slice sub-tool for leather parts (#1001, epic
 * #781): drag a knife across a part and it becomes two parts.
 *
 * A tool OWNS a controller instance and DRIVES it by composition, the same
 * arrangement `LeatherDeleteController` / `LeatherRoundController` /
 * `LeatherMirrorController` use.
 *
 * WHY THIS ISN'T CONSTRUCTION'S SLICE
 *
 * `DeleteSliceController` cuts construction lines, where an open result is a
 * perfectly good line — a cut just makes two of them. A part is a closed REGION,
 * so each side of the cut has to be re-closed along the cut path before it is a
 * part at all. That geometry lives in `src/model/pieceSlice.ts`; what's here is
 * the gesture, the live preview, and the commit.
 *
 * The knife cuts every visible part it crosses, in one commit, so a stroke drawn
 * across a stack separates all of them and a single undo puts them back.
 *
 * Deliberately no single-tap slice. Construction offers one (tap a curve, split
 * it there), but a tap carries no direction, and a part needs a cut LINE to
 * divide it — there's no sensible cut to infer from one point on an outline.
 */
import type paper from "paper";
import type { Scene, SceneSelection } from "../canvas/Scene";
import { isVisible, type Piece, type Project } from "../model/Project";
import { splitPieceWithStroke } from "../model/pieceSlice";
import { pieceDisplayOffset, translatePiece } from "../model/pieceTransform";
import { KnifeStroke, type KnifeModifiers } from "./knifeStroke";
import type { EditorContext } from "./EditorContext";
import { cssVar } from "../colors";

/** How far (project mm) the cursor must travel before a press counts as a cut.
 *  Matches the slop the construction knife uses, so a stray click on a part
 *  never silently halves it. */
const DRAG_SLOP_MM = 3;

/** Stroke colours for the live cut line. Read through functions so a theme
 *  switch is picked up. */
const ACCENT = () => cssVar("--canvas-accent");
const ACCENT_OVER_LEATHER = () => cssVar("--canvas-accent-leather");

export interface LeatherSliceHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: SceneSelection): void;
  /** The editor context, for the snap pipeline (#1013). `KnifeStroke` needs the
   *  snap SETTINGS, the scene's grid and the project's snappable geometry —
   *  which together are just the context, so passing it beats widening this
   *  interface with three accessors that only ever travel as a set. */
  getContext(): EditorContext;
}

export class LeatherSliceController {
  /** The in-flight knife stroke plus the dashed overlay drawn from it. Null
   *  between gestures. The stroke itself — including all snapping — lives in
   *  the shared `KnifeStroke` (#1013). */
  private drag: {
    stroke: KnifeStroke;
    preview: paper.Item | null;
    /** True once the cursor has travelled past `DRAG_SLOP_MM`. */
    engaged: boolean;
  } | null = null;

  constructor(private host: LeatherSliceHost) {}

  /** Test hook (#1001): the live cut stroke's sample count, or 0 when no drag
   *  is in flight. Lets e2e tell "the gesture never started" apart from "the
   *  cut didn't divide anything". */
  getStrokeLength(): number {
    return this.drag?.engaged ? this.drag.stroke.points().length : 0;
  }

  /** Test hook (#1013): whether the live cut is a straight, angle-snapped one.
   *  Distinguishes "Shift did nothing" from "Shift snapped to an angle that
   *  happens to match the drag". */
  isStraightCut(): boolean {
    return this.drag?.engaged ? this.drag.stroke.isStraight() : false;
  }

  handleDown(p: { x: number; y: number }, m: KnifeModifiers): boolean {
    const stroke = new KnifeStroke();
    stroke.begin(this.host.getContext(), p, m);
    this.drag = { stroke, preview: null, engaged: false };
    return true;
  }

  handleMove(p: { x: number; y: number }, m: KnifeModifiers): boolean {
    const drag = this.drag;
    if (!drag) return false;
    drag.stroke.extend(this.host.getContext(), p, m);
    // Engagement is measured on RAW cursor travel, not on the snapped stroke: a
    // press that snaps to a vertex a few mm away would otherwise read as a drag
    // the user never made.
    if (!drag.engaged) {
      if (drag.stroke.rawTravelMm() <= DRAG_SLOP_MM) return true;
      drag.engaged = true;
    }
    this.refreshPreview();
    return true;
  }

  /** Re-resolve the live cut when a modifier changes, so pressing or releasing
   *  the angle key switches between straight and freehand without waiting for
   *  the next pointer move. */
  handleModifiers(m: KnifeModifiers): void {
    const drag = this.drag;
    if (!drag) return;
    drag.stroke.replayModifiers(this.host.getContext(), m);
    if (drag.engaged) this.refreshPreview();
  }

  handleUp(): boolean {
    const drag = this.drag;
    if (!drag) return false;
    const points = drag.stroke.points().map((q) => ({ x: q.x, y: q.y }));
    const engaged = drag.engaged;
    this.clear();
    // A click that never became a drag isn't a cut. Falling through silently is
    // right: the user pointed at a part and let go, which asks for nothing.
    if (!engaged) return false;
    this.performSlice(points);
    return true;
  }

  /** Drop the in-flight stroke and its overlay. Tool switch / Escape / cancel. */
  clear(): void {
    if (this.drag?.preview) this.drag.preview.remove();
    this.drag?.stroke.end(this.host.getContext());
    this.drag = null;
  }

  // -------------------------------------------------------------------
  // Commit
  // -------------------------------------------------------------------

  /**
   * Cut every visible part the stroke crossed.
   *
   * Each part is tested in its OWN space and its halves are moved back to where
   * it was drawn. A construction-LINKED part is drawn at a cut-layout offset
   * that isn't baked into its stored geometry (#786), and the halves come out
   * unlinked — `clonePieceForDuplicate` drops the link, because a linked half
   * would re-derive from the same scaffold and undo the cut. Without the
   * round-trip they'd snap back to the un-laid-out position on release.
   */
  private performSlice(samples: Array<{ x: number; y: number }>): void {
    if (samples.length < 2) return;
    const scope = this.host.getScope();
    const project = this.host.getProject();
    const view = this.host.getScene().getPartLayoutView();

    // Plan the whole sweep before touching the project, so one commit carries
    // every cut and a single undo takes the sweep back.
    const cuts = new Map<string, [Piece, Piece]>();
    for (const piece of project.pieces) {
      if (!isVisible(piece)) continue;
      const off = pieceDisplayOffset(piece, view);
      const local =
        off.dx || off.dy
          ? samples.map((s) => ({ x: s.x - off.dx, y: s.y - off.dy }))
          : samples;
      const halves = splitPieceWithStroke(piece, local, scope, [
        `${piece.name} A`,
        `${piece.name} B`,
      ]);
      if (!halves) continue;
      cuts.set(
        piece.id,
        off.dx || off.dy
          ? [
              translatePiece(halves[0], off.dx, off.dy, scope),
              translatePiece(halves[1], off.dx, off.dy, scope),
            ]
          : halves,
      );
    }
    if (cuts.size === 0) return;

    const newIds: string[] = [];
    for (const [, halves] of cuts) newIds.push(halves[0].id, halves[1].id);

    this.host.commit(cuts.size > 1 ? "Slice parts" : "Slice part", (proj) => {
      const pieces: Piece[] = [];
      for (const piece of proj.pieces) {
        const halves = cuts.get(piece.id);
        // Each half takes the source's place in draw order, so the cut doesn't
        // shuffle what sits on top of what.
        if (halves) pieces.push(halves[0], halves[1]);
        else pieces.push(piece);
      }
      return { ...proj, pieces };
    });

    // Select every new half. The cut runs down the middle of what used to be
    // one outline, so seeing both sides highlighted is the clearest possible
    // confirmation that it landed — and where.
    this.host.setSelection({
      pieceId: newIds[newIds.length - 1] ?? null,
      pieceIds: newIds,
      decorationId: null,
      holeId: null,
      cutoutId: null,
      stitchId: null,
      constructionIds: [],
      constructionGroupIds: [],
      tracingImageIds: [],
    });
  }

  // -------------------------------------------------------------------
  // Live preview
  // -------------------------------------------------------------------

  /** Redraw the dashed line tracing the knife, so the user can see where the
   *  cut will land before committing to it. */
  private refreshPreview(): void {
    const drag = this.drag;
    if (!drag) return;
    const scope = this.host.getScope();
    if (drag.preview) {
      drag.preview.remove();
      drag.preview = null;
    }
    const points = drag.stroke.points();
    if (points.length < 2) return;
    try {
      const path = new scope.Path({ insert: true });
      for (const s of points) path.add(new scope.Point(s.x, s.y));
      // One verdict for the whole line, taken at its MIDPOINT: a cut stroke
      // starts and ends off the part but spends its length over leather, so the
      // midpoint is what the accent has to stay legible against. Two-tone
      // stroking would be worse than either choice.
      //
      // Measured along the path rather than by picking the middle SAMPLE — an
      // angle-snapped cut is two points, whose "middle sample" is the far end,
      // sitting off the leather and reading the accent backwards.
      const mid = path.getPointAt(path.length / 2) ?? path.firstSegment.point;
      path.strokeColor = new scope.Color(
        this.host.getScene().isPointOverLeather({ x: mid.x, y: mid.y })
          ? ACCENT_OVER_LEATHER()
          : ACCENT(),
      );
      path.strokeWidth = 1.6;
      path.strokeScaling = false;
      path.dashArray = [6, 4];
      path.fillColor = null;
      path.data = { ephemeral: true };
      drag.preview = path;
    } catch {
      /* preview only — an unparseable stroke just doesn't draw */
    }
  }
}
